import logger from '../utils/logger';
import { 
  IInterruptedBlankSession, IBlankSessionChunk,
  saveBlankSession, updateBlankSessionSegments, deleteBlankSession,
  saveBlankSessionChunks, markBlankSessionInterrupted,
} from '../idb/chunk-db';

interface ChunkBuffer {
  streamType: 'screen' | 'webcam' | 'audio';
  segmentIndex: number;
  chunkIndex: number;
  blob: Blob;
}

export class BlankSessionPersistence {
  private recordingId: string | null = null;
  private chunkBuffer: ChunkBuffer[] = [];
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private chunkCounters: Map<string, number> = new Map();
  private isActive: boolean = false;
  private segmentProvider: (() => { screen: any[]; webcam: any[]; audio: any[] }) | null = null;
  
  private static FLUSH_INTERVAL_MS = 5000;
  
  setSegmentProvider(provider: (() => { screen: any[]; webcam: any[]; audio: any[] }) | null): void {
    this.segmentProvider = provider;
  }
  
  async startSession(params: {
    recordingId: string;
    layout: any;
    micEnabled: boolean;
    hasDedicatedAudioStream: boolean;
    mirroredWebcam: boolean;
    avatarWebcam: boolean;
    avatarUrl: string | null;
    edgeToEdge: boolean;
    screenCornerRadius: number;
    quality: string;
  }): Promise<void> {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    
    this.recordingId = params.recordingId;
    this.chunkBuffer = [];
    this.chunkCounters.clear();
    this.isActive = true;
    
    const session: IInterruptedBlankSession = {
      recordingId: params.recordingId,
      layout: params.layout,
      micEnabled: params.micEnabled,
      screenSegments: [],
      webcamSegments: [],
      audioSegments: [],
      hasDedicatedAudioStream: params.hasDedicatedAudioStream,
      mirroredWebcam: params.mirroredWebcam,
      avatarWebcam: params.avatarWebcam,
      avatarUrl: params.avatarUrl,
      edgeToEdge: params.edgeToEdge,
      screenCornerRadius: params.screenCornerRadius,
      quality: params.quality,
      createdAt: Date.now(),
      lastUpdatedAt: Date.now(),
      status: 'recording',
    };
    
    await saveBlankSession(session);
    
    this.flushInterval = setInterval(() => {
      this.flushChunks();
      this.persistCurrentSegments();
    }, BlankSessionPersistence.FLUSH_INTERVAL_MS);
    
    logger.info('[BlankSessionPersistence] Session started', { recordingId: params.recordingId });
  }
  
  bufferChunk(streamType: 'screen' | 'webcam' | 'audio', segmentIndex: number, blob: Blob): void {
    if (!this.isActive || !this.recordingId) return;
    
    const counterKey = `${streamType}:${segmentIndex}`;
    const chunkIndex = this.chunkCounters.get(counterKey) || 0;
    this.chunkCounters.set(counterKey, chunkIndex + 1);
    
    this.chunkBuffer.push({
      streamType,
      segmentIndex,
      chunkIndex,
      blob,
    });
  }
  
  onSegmentStart(streamType: 'screen' | 'webcam' | 'audio', segmentIndex: number): void {
    const counterKey = `${streamType}:${segmentIndex}`;
    this.chunkCounters.set(counterKey, 0);
  }
  
  async flushChunks(): Promise<void> {
    if (!this.recordingId || this.chunkBuffer.length === 0) return;
    
    const chunksToFlush = [...this.chunkBuffer];
    this.chunkBuffer = [];
    
    try {
      const idbChunks: IBlankSessionChunk[] = chunksToFlush.map(c => ({
        id: `${this.recordingId}:${c.streamType}:${c.segmentIndex}:${c.chunkIndex}`,
        recordingId: this.recordingId!,
        streamType: c.streamType,
        segmentIndex: c.segmentIndex,
        chunkIndex: c.chunkIndex,
        blob: c.blob,
        createdAt: Date.now(),
      }));
      
      await saveBlankSessionChunks(idbChunks);
      logger.info('[BlankSessionPersistence] Flushed chunks to IDB', { 
        count: idbChunks.length, 
        recordingId: this.recordingId 
      });
    } catch (error) {
      logger.warn('[BlankSessionPersistence] Failed to flush chunks (non-critical)', error);
      this.chunkBuffer.unshift(...chunksToFlush);
    }
  }
  
  private async persistCurrentSegments(): Promise<void> {
    if (!this.recordingId || !this.segmentProvider) return;
    try {
      const segments = this.segmentProvider();
      await updateBlankSessionSegments(
        this.recordingId,
        segments.screen.map(s => ({ ...s })),
        segments.webcam.map(s => ({ ...s })),
        segments.audio.map(s => ({ ...s }))
      );
    } catch (error) {
      logger.warn('[BlankSessionPersistence] Periodic segment persist failed (non-critical)', error);
    }
  }
  
  async updateSegments(
    screenSegments: any[],
    webcamSegments: any[],
    audioSegments: any[]
  ): Promise<void> {
    if (!this.recordingId) return;
    try {
      await updateBlankSessionSegments(this.recordingId, screenSegments, webcamSegments, audioSegments);
    } catch (error) {
      logger.warn('[BlankSessionPersistence] Failed to update segments (non-critical)', error);
    }
  }
  
  async endSession(): Promise<void> {
    if (!this.recordingId) return;
    
    this.isActive = false;
    this.segmentProvider = null;
    
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    
    try {
      await deleteBlankSession(this.recordingId);
      logger.info('[BlankSessionPersistence] Session cleaned up', { recordingId: this.recordingId });
    } catch (error) {
      logger.warn('[BlankSessionPersistence] Failed to clean up session', error);
    }
    
    this.recordingId = null;
    this.chunkBuffer = [];
    this.chunkCounters.clear();
  }
  
  async stopSession(
    screenSegments: any[],
    webcamSegments: any[],
    audioSegments: any[]
  ): Promise<void> {
    if (!this.recordingId) return;
    
    this.segmentProvider = null;
    
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    
    await this.flushChunks();
    
    await this.updateSegments(screenSegments, webcamSegments, audioSegments);
    
    try {
      await markBlankSessionInterrupted(this.recordingId);
    } catch (error) {
      logger.warn('[BlankSessionPersistence] Failed to mark session interrupted (non-critical)', error);
    }
    
    logger.info('[BlankSessionPersistence] Session stopped and marked interrupted, chunks flushed', { recordingId: this.recordingId });
  }
  
  getRecordingId(): string | null {
    return this.recordingId;
  }
  
  isSessionActive(): boolean {
    return this.isActive;
  }
}

export const blankSessionPersistence = new BlankSessionPersistence();
